const isPresent = (value) => value !== null && value !== undefined && value !== '';

const compact = (value) =>
  Object.fromEntries(
    Object.entries(value).filter(([, item]) => {
      if (!isPresent(item)) {
        return false;
      }

      return !Array.isArray(item) || item.length > 0;
    })
  );

/**
 * The category page's own url: storeFrontAbsoluteUrl (the store's root, e.g.
 * https://host/stores/cName) plus /products, /products/<category>, or
 * /products/<category>/<subcategory>.
 */
function buildCategoryUrl(storeFrontAbsoluteUrl, category, subcategory) {
  if (!storeFrontAbsoluteUrl) {
    return undefined;
  }

  const segments = ['products', category?.key, subcategory].filter(Boolean);

  return `${storeFrontAbsoluteUrl}/${segments.join('/')}`;
}

function buildProductUrl(storeFrontAbsoluteUrl, product) {
  const productId = product?.cName ?? product?.id ?? product?._id;

  if (!storeFrontAbsoluteUrl || !productId) {
    return undefined;
  }

  return `${storeFrontAbsoluteUrl}/product/${productId}`;
}

const buildCategoryPageJsonLd = (categoryData, storeFrontAbsoluteUrl) => {
  const { dispensary, category, subcategory, products = [], totalCount } = categoryData;
  const name = category?.label ?? dispensary?.name;
  const absoluteUrl = buildCategoryUrl(storeFrontAbsoluteUrl, category, subcategory);

  // Filter to resolvable products BEFORE assigning position so positions stay sequential/gapless
  // (starting at 1) even if a product's url couldn't be resolved.
  const itemListElement = products
    .map((product) => ({
      name: product?.name,
      url: buildProductUrl(storeFrontAbsoluteUrl, product),
    }))
    .filter((product) => product.url)
    .map((product, index) =>
      compact({
        '@type': `ListItem`,
        position: index + 1,
        url: product.url,
        name: product?.name,
      })
    );

  const numberOfItems = Number.isFinite(totalCount) ? totalCount : itemListElement.length;

  return compact({
    '@context': `https://schema.org`,
    '@type': `CollectionPage`,
    '@id': absoluteUrl ? `${absoluteUrl}#category` : undefined,
    url: absoluteUrl,
    name,
    // Not run through compact(): an empty itemListElement is a valid (if unusual) ItemList and
    // should stay explicit rather than being dropped by compact's "drop empty arrays" rule.
    mainEntity: {
      '@type': `ItemList`,
      numberOfItems,
      itemListElement,
    },
  });
};

export default buildCategoryPageJsonLd;
